import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { clientOnboardingSchema, clientOnboardingType } from "../../validator";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import ColorPickerDialog from "@/components/custom/ColorPickerDialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { capitalize, omit } from "lodash";
import { CloudUpload, LoaderCircle, Pencil, Trash2 } from "lucide-react";
import Dropzone from "react-dropzone";
import fonts from "@/assets/fonts.json";
import { Combobox } from "@/components/ui/combobox";
import { useMutation, useQuery } from "@tanstack/react-query";
import useSonner from "@/hooks/use-sonner";
import { updateApp } from "@/types";
import { updateClientApp, clientOnboarded } from "@/http";
import useCurrentUserStore from "@/store";
import { ScrollArea } from "@/components/ui/scroll-area";

type PropTypes = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<clientOnboardingType, any, undefined>;
  nextTrigger: VoidFunction;
};

const Step4Content: React.FC<PropTypes> = (props) => {
  const [hover, setHover] = React.useState(false);

  const step4Data = props.form.watch("step4");
  const [isLoading, setisLoading] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [localImage, setlocalImage] = useState<any>("");
  const { addToast } = useSonner();
  const { appId, setIsOnboardingComplete } = useCurrentUserStore();

  const { refetch } = useQuery({
    queryKey: ["clientOnboarded"],
    queryFn: () => clientOnboarded(),
    enabled: false,
    retry: 2,
  });

  const updateApp = useMutation<
    Awaited<ReturnType<typeof updateClientApp>>, // Response type
    unknown, // Error type
    updateApp // Variables type
  >({
    mutationFn: updateClientApp,
    onSuccess: async () => {
      const { data: fetchedData } = await refetch();
      if (fetchedData?.data?.data?.result) {
        addToast({
          message: "App created successfully and you are Onboarded.",
          type: "success",
        });
        setisLoading(false);
        props.form.clearErrors();
        props.nextTrigger();
      } else {
        setisLoading(false);
        addToast({
          message: fetchedData?.data.data.message || "Something went wrong!",
          type: "error",
        });
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => {
      setisLoading(false);
      //  setIsSignInWithGoogle(false);
      addToast({
        message: err?.response?.data?.data?.message || "Something went wrong!",
        type: "error",
      });
    },
  });

  const handleUploadImage = async (image: string) => {
    const formData = new FormData();

    formData.append("path", "public");
    formData.append("images", image);

    return await fetch(`${import.meta.env.VITE_BUCKET_SERVICE}/image`, {
      method: "POST",
      headers: {
        "x-secret": import.meta.env.VITE_BUCKET_SERVICE_SECRET,
      },
      body: formData,
    });
  };
  const handleNextClick = async () => {
    const resolve = clientOnboardingSchema.shape["step4"].safeParse(step4Data);
    if (resolve.success) {
      setisLoading(true);
      if (localImage !== "") {
        const imageUrl = await handleUploadImage(localImage);
        const formatData = await imageUrl.json();
        props.form.setValue(
          "step4.logo",
          formatData?.data?.result?.[0]?.publicURL || ""
        );
      }

      updateApp.mutate({
        id: appId,
        theme: omit(step4Data, ["logo"]),
        logo: step4Data?.logo || "",
        localization: {},
      });
      setlocalImage("");
      setIsOnboardingComplete(true);
      props.nextTrigger();
    }
  };

  const handleEdit = () => {
    // Trigger the file input click
    const fileInput = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  return (
    <ScrollArea
      className="h-[85vh] "
      scrollBarClassName="bg-gray-200"
      scrollThumbClassName="bg-gray-300"
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <h2 className="font-semibold text-[28px]/[32px]">
            Customise your Blog App
          </h2>
          <p className="text-sm text-muted-foreground">
            Personalize your blog’s look and feel by selecting colors, fonts,
            and theme mode.
          </p>
        </div>
        <div className="flex flex-col gap-4">
          {/* Theme picker */}
          <FormField
            control={props.form.control}
            name="step4.appearance"
            render={({ field, fieldState }) => (
              <FormItem className="space-y-3">
                <FormLabel>Choose your theme</FormLabel>
                <RadioGroup
                  className="w-full grid grid-cols-2 gap-4"
                  value={field.value}
                  onValueChange={(e) => {
                    field.onChange(e);
                    const bgColor = e === "light" ? "#fff" : "#000";
                    const fontColor = e !== "light" ? "#fff" : "#000";
                    props.form.setValue("step4.backgroundColor", bgColor);
                    props.form.setValue("step4.primaryColor", fontColor);
                  }}
                >
                  {["light", "dark"]?.map((appearance) => (
                    <FormLabel
                      key={appearance}
                      htmlFor={appearance + "-appearance"}
                      className={cn(
                        "flex flex-col gap-[10px] border-2 border-secondary rounded-xl p-3",
                        {
                          "border-destructive": fieldState.error,
                          "border-primary": step4Data.appearance === appearance,
                        }
                      )}
                    >
                      {/* radio button */}
                      <FormItem className="flex items-center justify-between space-y-0">
                        <FormControl>
                          <RadioGroupItem
                            id={appearance + "-appearance"}
                            value={appearance}
                            className={cn({
                              "border-destructive": fieldState.error,
                            })}
                          />
                        </FormControl>
                        <span className="m-0">
                          {capitalize(appearance)} Mode
                        </span>
                      </FormItem>
                    </FormLabel>
                  ))}
                </RadioGroup>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Primary Color */}
          <FormField
            control={props.form.control}
            name={`step4.primaryColor`}
            render={({ field }) => (
              <FormItem className="flex items-center justify-between">
                <FormLabel>Primary Color</FormLabel>
                <FormControl>
                  <ColorPickerDialog
                    value={step4Data.primaryColor}
                    onChange={(e) => {
                      field.onChange(e);
                      props.form.setValue("step4.primaryColor", e);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Background Color */}
          <FormField
            control={props.form.control}
            name={`step4.backgroundColor`}
            render={({ field }) => (
              <FormItem className="flex items-center justify-between">
                <FormLabel>Background Color</FormLabel>
                <FormControl>
                  <ColorPickerDialog
                    value={step4Data.backgroundColor}
                    onChange={(e) => {
                      field.onChange(e);
                      props.form.setValue("step4.backgroundColor", e);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Logo */}
          <FormField
            control={props.form.control}
            name={"step4.logo"}
            render={({ field }) => (
              <FormItem className="flex flex-col gap-2">
                <FormLabel>Logo</FormLabel>

                <Dropzone
                  multiple={false}
                  onDrop={(acceptedFiles) => {
                    if (!acceptedFiles.length) return;
                    setlocalImage(acceptedFiles?.[0]);
                    const file = acceptedFiles?.[0];
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      field.onChange(e?.target?.result as string);
                    };
                    reader.readAsDataURL(file);
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <FormControl {...getRootProps()}>
                      <FormLabel
                        className={cn(
                          "flex flex-col items-center justify-center gap-2 border rounded-md p-4 text-center cursor-pointer relative",
                          "transition-all duration-200 ease-in-out",
                          {
                            "h-[200px]": field.value,
                            "hover:bg-accent hover:text-accent-foreground":
                              !field.value,
                          }
                        )}
                        style={{
                          backgroundImage: field.value
                            ? `url(${field.value})`
                            : "none",
                          backgroundSize: "contain",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                        }}
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}
                      >
                        {!field.value && (
                          <>
                            <div className="flex items-center justify-center p-2 py-1 border border-gray-300 rounded-md">
                              <CloudUpload className="w-6 h-6" />
                            </div>
                            <p className="text-sm text-muted-foreground">
                              <span className="text-primary">
                                Click to upload
                              </span>
                              <span className="opacity-60">
                                <span> or drag & drop</span>
                                <span className="text-xs">
                                  {" "}
                                  (recommended 800x400px)
                                </span>
                              </span>
                            </p>
                          </>
                        )}
                        {field.value && hover && (
                          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center space-x-2 rounded-md">
                            <Button
                              variant="secondary"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent the click from propagating to Dropzone
                                handleEdit();
                              }}
                              className="w-10 h-10"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent the click from propagating to Dropzone
                                field.onChange("");
                              }}
                              className="w-10 h-10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        <Input {...getInputProps()} />
                      </FormLabel>
                    </FormControl>
                  )}
                </Dropzone>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Header Font */}
          <FormField
            control={props.form.control}
            name={"step4.headerFont"}
            defaultValue={
              props.form.formState.defaultValues?.["step4"]?.headerFont
            }
            render={({ field }) => (
              <FormItem className="flex flex-col gap-2">
                <FormLabel>Font Style</FormLabel>
                <div className="flex items-center justify-between">
                  <FormDescription>Page Headers</FormDescription>
                  <Combobox
                    className="w-[40%]"
                    selectedOption={field.value}
                    onOptionSelect={field.onChange}
                    options={fonts?.map((font) => ({
                      label: font,
                      value: font,
                    }))}
                  />
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          {/* Body Font */}
          <FormField
            control={props.form.control}
            name={"step4.bodyFont"}
            defaultValue={
              props.form.formState.defaultValues?.["step4"]?.bodyFont
            }
            render={({ field }) => (
              <FormItem className="flex flex-col gap-2 w">
                <div className="flex items-center justify-between">
                  <FormDescription>Headings & Paragraphs</FormDescription>
                  <Combobox
                    className="w-1/3"
                    selectedOption={field.value}
                    onOptionSelect={field.onChange}
                    options={fonts?.map((font) => ({
                      label: font,
                      value: font,
                    }))}
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* next button */}
        <Button
          id="app-creation-step4"
          type="submit"
          disabled={isLoading}
          className="w-full"
          onClick={handleNextClick}
        >
          {isLoading && <LoaderCircle className="animate-spin" />}
          Save
        </Button>
      </div>
    </ScrollArea>
  );
};

export default Step4Content;
